import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import useScrollToTop from '@/hooks/useScrollToTop';

export default function ArticlePage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </span>
            <p className="text-foreground-500 mb-4">文章不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '91网址',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.91wangzhi.com/article/${article.id}`,
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: category ? category.name : '文章', to: category ? `/category/${category.slug}` : undefined },
                    { label: article.title },
                  ]}
                />
              </div>

              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 text-sm text-foreground-500">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                  <span>·</span>
                  <span>{article.author}</span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden h-64 md:h-96">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-h3:text-lg prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedArticles.length > 0 && (
                <section className="mt-16 pt-10 border-t border-background-200/70">
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                    相关文章推荐
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                      >
                        <div className="h-40 overflow-hidden">
                          <img
                            src={rel.imageUrl}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-foreground-900 font-heading line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {rel.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}